import React from "react";
import PropTypes from "prop-types";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { formatMoney, formatMoneyCompact } from "../../Budgets/formatters";
import { ROLE_LABELS } from "../policyBuilderState";

/** Human-readable triggered rule: "Change exceeds $50,000 threshold" or "Any amount" etc. */

const describeContext = (tier) => {
  if (!tier?.context?.near_term_only) return null;
  const days = tier.context.within_days ?? 90;
  return days;
};

const getTierChain = (tier) => {
  const chain = tier?.chain ?? tier?.Chain;
  return Array.isArray(chain) ? chain : [];
};

const collectChainLabels = (tier) =>
  getTierChain(tier).map((s) => {
    const step = s && typeof s === "object" ? s : {};
    const label = step.label ?? step.Label;
    const role = step.role ?? step.Role;
    return label || ROLE_LABELS[role] || (role && String(role)) || "Approver";
  });

/** Best-effort extraction of policy name from snapshot + explicit prop. */
const getPolicyNameFromSnapshot = (policySnapshot, explicitName) => {
  if (explicitName) return explicitName;
  if (!policySnapshot || typeof policySnapshot !== "object") return "";

  const meta =
    policySnapshot.metadata ||
    policySnapshot.Metadata ||
    policySnapshot.meta ||
    policySnapshot.Meta ||
    {};

  const metaName = meta.name || meta.Name;
  if (metaName) return metaName;

  const topLevelName = policySnapshot.name || policySnapshot.Name;
  return topLevelName || "";
};

/** Pick the tier that matches this request: tier whose chain length equals stepsCount (same logic as ApprovalStepTimeline). */
const getDisplayTier = (policySnapshot, stepsCount) => {
  const tiers = policySnapshot?.tiers ?? policySnapshot?.Tiers;
  if (!Array.isArray(tiers) || tiers.length === 0) return null;
  if (typeof stepsCount === "number" && stepsCount > 0) {
    const matching = tiers.find((t) => getTierChain(t).length === stepsCount);
    if (matching) return matching;
  }
  return tiers[0];
};

/** One-line triggered rule using change amount + tier bounds (+ optional time constraint). */
const getTriggeredRuleLine = (tier, currency, impactAmount) => {
  if (!tier) return "";
  const c = currency || "EUR";

  const hasChange = typeof impactAmount === "number";
  const absChangeCents = hasChange ? Math.round(Math.abs(impactAmount) * 100) : null;
  const formattedChange = hasChange ? formatMoney(absChangeCents, c) : null;

  const days = describeContext(tier);
  const timePhrase = days != null ? ` within the next ${days} days` : "";

  let changePart = "";
  if (formattedChange) {
    changePart = `Change of ${formattedChange}${timePhrase}`;
  }

  const hasMin = Number.isFinite(tier.min_amount);
  const hasMax = typeof tier.max_amount === "number" && tier.max_amount > 0;
  const minLabel = hasMin
    ? formatMoney(Math.round(tier.min_amount * 100), c)
    : null;
  const maxLabel = hasMax
    ? formatMoney(Math.round(tier.max_amount * 100), c)
    : null;

  let thresholdPart = "";
  if (tier.any_amount) {
    thresholdPart = "is subject to approval for any amount";
  } else if (!hasMin && hasMax && maxLabel) {
    // Max only
    thresholdPart = `falls within the ${maxLabel} approval threshold`;
  } else if (hasMin && hasMax && minLabel && maxLabel) {
    // Min + max
    thresholdPart = `falls between the ${minLabel} and ${maxLabel} approval thresholds`;
  } else if (hasMin && minLabel) {
    // Min only
    thresholdPart = `exceeds the ${minLabel} approval threshold`;
  } else if (minLabel || maxLabel) {
    thresholdPart = `is subject to the ${minLabel || maxLabel} approval threshold`;
  } else {
    thresholdPart = "is subject to approval";
  }

  if (!changePart) {
    return `Change ${thresholdPart}`;
  }
  return `${changePart} ${thresholdPart}`;
};

/** Format tier range for footer: "$50k – $200k" or "Any amount" or "$50k+". */
const getTierRangeLine = (tier, currency = "EUR") => {
  if (!tier) return "";
  if (tier.any_amount) return "Any amount";
  const minCents = Number.isFinite(tier.min_amount)
    ? Math.round(tier.min_amount * 100)
    : null;
  const maxCents =
    typeof tier.max_amount === "number" && tier.max_amount > 0
      ? Math.round(tier.max_amount * 100)
      : null;
  if (minCents != null && maxCents != null) {
    return `${formatMoneyCompact(minCents, currency)} – ${formatMoneyCompact(maxCents, currency)}`;
  }
  if (minCents != null) return `${formatMoneyCompact(minCents, currency)}+`;
  if (maxCents != null) return `up to ${formatMoneyCompact(maxCents, currency)}`;
  return "";
};

/** One-line approval chain: step labels joined by arrow. Uses tier's chain array; fallback to top-level chain. */
const getApprovalChainLine = (tier, policySnapshot) => {
  const fromTier = tier ? getTierChain(tier) : [];
  if (fromTier.length > 0) {
    return collectChainLabels(tier).join(" → ");
  }
  const topChain = policySnapshot?.chain ?? policySnapshot?.Chain;
  if (Array.isArray(topChain) && topChain.length > 0) {
    return topChain
      .map((s) => {
        const step = s && typeof s === "object" ? s : {};
        const label = step.label ?? step.Label;
        const role = step.role ?? step.Role;
        return label || ROLE_LABELS[role] || (role && String(role)) || "Approver";
      })
      .join(" → ");
  }
  return "";
};

const GovernancePolicyPanel = ({ policySnapshot, policyName, steps, impactAmount }) => {
  const hasPolicyInfo = policySnapshot && Object.keys(policySnapshot).length > 0;

  if (!hasPolicyInfo) {
    return (
      <div className="bg-card border border-card-bd rounded-card p-5">
        <div className="flex items-center gap-2 mb-3">
          <FontAwesomeIcon
            icon={icon({
              name: "shield-check",
              family: "sharp",
              style: "light",
            })}
            className="text-text-2 text-sm"
          />
          <h3 className="text-sm font-semibold text-text">
            Governance Policy
          </h3>
        </div>
        <div className="flex flex-col items-center justify-center py-6 text-center">
          <FontAwesomeIcon
            icon={icon({
              name: "magnifying-glass",
              family: "sharp",
              style: "light",
            })}
            className="text-text-3 text-xl mb-2"
          />
          <p className="text-sm font-medium text-text-2">
            Policy info unavailable
          </p>
          <p className="text-xs text-text-3 mt-0.5">
            Contact the governance administrator for guidelines
          </p>
        </div>
      </div>
    );
  }

  const currency = policySnapshot.currency || "EUR";
  const stepsCount = Array.isArray(steps) ? steps.length : 0;
  const displayTier = getDisplayTier(policySnapshot, stepsCount);
  const triggeredRuleLine = getTriggeredRuleLine(displayTier, currency, impactAmount);
  const approvalChainLine = getApprovalChainLine(displayTier, policySnapshot);
  const policyNameDisplay = getPolicyNameFromSnapshot(policySnapshot, policyName);
  const tierRangeLine = getTierRangeLine(displayTier, currency);

  return (
    <div className="bg-card border border-card-bd rounded-card p-5 min-h-[225px]">
      <div className="flex items-center gap-2 mb-4">
        <FontAwesomeIcon
          icon={icon({
            name: "shield-check",
            family: "sharp",
            style: "light",
          })}
          className="text-text-2 text-sm"
        />
        <h3 className="text-sm font-semibold text-text">
          {policyNameDisplay
            ? `Governance Policy \u2013 ${policyNameDisplay}`
            : "Governance Policy"}
        </h3>
      </div>

      {triggeredRuleLine ? (
        <div className="mb-3">
          <p className="text-xs uppercase tracking-wider text-text-2 mb-0.5">
            Triggered rule / threshold
          </p>
          <p className="text-sm text-text">{triggeredRuleLine}</p>
        </div>
      ) : null}

      {approvalChainLine ? (
        <div className="mb-3">
          <p className="text-xs uppercase tracking-wider text-text-2 mb-0.5">
            Required approval chain
          </p>
          <p className="text-sm text-text">{approvalChainLine}</p>
        </div>
      ) : null}

      {tierRangeLine ? (
        <p className="text-xs text-text-2 mt-3 pt-3 border-t border-border">
          Based on policy tier: {tierRangeLine}
        </p>
      ) : null}
    </div>
  );
};

GovernancePolicyPanel.propTypes = {
  policySnapshot: PropTypes.object,
  policyName: PropTypes.string,
  steps: PropTypes.arrayOf(PropTypes.object),
  impactAmount: PropTypes.number,
};

export default GovernancePolicyPanel;
